import { useCallback, useState } from 'react';
import api from '../../api/axiosInstance';
import useDiscoveryFetch from '../Discovery/useDiscoveryFetch';

// Looks up (never creates) the community for a Country/City exactly once per
// (type, refId), regardless of how many presentation components (mobile hero,
// desktop header, ...) render the resulting CTA. Callers must render the
// returned state/handlers rather than mounting their own fetch per breakpoint,
// so a page that renders both a mobile and a desktop section for the same
// target never issues two GET /api/conversations/communities/:type/:refId calls.
export default function useCommunityMembership({ type, refId }) {
    // `available` distinguishes "target is active but has no community yet"
    // (200, conversation: null — Join should still be offered) from "target is
    // deactivated/unknown" (404 — no CTA at all).
    const [state, setState] = useState({
        loading: true, error: '', available: false, conversation: null, isMember: false, memberCount: 0,
    });
    const [joining, setJoining] = useState(false);
    const [joinError, setJoinError] = useState('');

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        if (!type || !refId) {
            setState({ loading: false, error: '', available: false, conversation: null, isMember: false, memberCount: 0 });
            return undefined;
        }

        const load = async () => {
            try {
                if (!isReconnect) setState((prev) => ({ ...prev, loading: true, error: '' }));
                const res = await api.get(`/api/conversations/communities/${type}/${refId}`, { signal });
                if (!isCurrent()) return;
                setState({
                    loading: false,
                    error: '',
                    available: true,
                    conversation: res.data?.conversation || null,
                    isMember: !!res.data?.isMember,
                    memberCount: res.data?.memberCount || 0,
                });
            } catch (err) {
                if (!isCurrent()) return;
                // A 404 means the target isn't joinable (deactivated/unknown) — not
                // an error state, just "no CTA to offer".
                if (err?.response?.status === 404) {
                    setState({ loading: false, error: '', available: false, conversation: null, isMember: false, memberCount: 0 });
                    return;
                }
                console.error('Error fetching community status:', err);
                setState((prev) => ({ ...prev, loading: false, error: 'Failed to load community status.' }));
            }
        };

        load();
        return undefined;
    }, [type, refId]);

    const join = useCallback(async () => {
        if (!type || !refId || joining) return null;
        setJoining(true);
        setJoinError('');
        try {
            const res = await api.post(`/api/conversations/communities/${type}/${refId}/join`);
            const conversation = res.data?.conversation || null;
            setState({
                loading: false, error: '', available: true, conversation, isMember: true,
                memberCount: conversation?.memberCount || 0,
            });
            return conversation;
        } catch (err) {
            console.error('Error joining community:', err);
            setJoinError(err?.response?.data?.msg || 'Failed to join community. Please try again.');
            return null;
        } finally {
            setJoining(false);
        }
    }, [type, refId, joining]);

    return { ...state, joining, joinError, join };
}
